import React, {useContext, useState} from "react";
import {FieldValues, useForm} from "react-hook-form";
import {POST} from "../../../Hooks/RequestV2";
import {UseFormSetError} from "react-hook-form/dist/types/form";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {SavedCartModal} from "../SavedCarts/TransferSavedCartToCart";
import {Throbber} from "../../../Components/Throbber";
import {UserContext} from "../../../Contexts/UserContext";

interface fileTypeData {
    label: string,
    format: string
}

function useFileTypes(): Record<string, fileTypeData> {
    const {t} = useTranslation();

    return {
        CsvData: {
            label: t('user-page.invoices.download.types.CsvData'),
            format: 'csv-data'
        },
        CsvDot: {
            label: t('user-page.invoices.download.types.CsvDot'),
            format: 'csv-dot'
        },
        CsvDotNoHeader: {
            label: t('user-page.invoices.download.types.CsvDotNoHeader'),
            format: 'csv-dot-no-header'
        },
        CsvCommon: {
            label: t('user-page.invoices.download.types.CsvCommon'),
            format: 'csv-common'
        },
        CsvCommonNoHeader: {
            label: t('user-page.invoices.download.types.CsvCommonNoHeader'),
            format: 'csv-common-no-header'
        },
        EDIXML: {
            label: t('user-page.invoices.download.types.EDIXML'),
            format: 'edi-xml'
        },
        PCMARKET: {
            label: t('user-page.invoices.download.types.PCMARKET'),
            format: 'pc-market'
        },
        OptimaComarch: {
            label:  t('user-page.invoices.download.types.OptimaComarch'),
            format: 'optima-comarch'
        },
        SUBIEKT: {
            label: t('user-page.invoices.download.types.SUBIEKT'),
            format: 'subiekt'
        },
        EAN: {
            label: t('user-page.invoices.download.types.EAN'),
            format: 'ean'
        },
        HOMER: {
            label:  t('user-page.invoices.download.types.HOMER'),
            format: 'homer'
        },
        XLS: {
            label: t('user-page.invoices.download.types.XLS'),
            format: 'xls'
        },
        PDF: {
            label: t('user-page.invoices.download.types.PDF'),
            format: 'pdf'
        },
        KC: {
            label:  t('user-page.invoices.download.types.KC'),
            format: 'kc-firma'
        }
    };
}

function downloadFile(
    data: FieldValues,
    setError: UseFormSetError<FieldValues>,
    invoiceId: number,
    fileTypes: any,
    t: any): void {

    data.id = invoiceId;

    if (data.format === fileTypes.EDIXML.format && data.payer_code === ''){
        setError('payer_code', {message: t('user-page.invoices.download.payer_code_error')});

        return;
    } else if (data.format !== fileTypes.EDIXML.format) {
        data.payer_code = null;
    }

    function getFileName(contentDisposition: string): string | null{
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        let matches = filenameRegex.exec(contentDisposition);

        if (matches != null && matches[1]) {
            return matches[1].replace(/['"]/g, '');
        }

        return null;
    }

    POST('/user/invoice/download-file', data)
        .then((response) => {
            if (response.status === 200){
                response.blob().then((blob) => {
                    const contentDisposition = response.headers.get('Content-Disposition');
                    const fileName = getFileName(contentDisposition ?? t('user-page.invoices.download.file_name')+'.csv') ?? t('user-page.invoices.download.file_name')+'.csv';
                    const fileNameArray = fileName?.split('.') ?? [t('user-page.invoices.download.file_name'), 'csv'];

                    const blobWithType = new Blob([blob], { type: 'application/octet-stream' });

                    const url = URL.createObjectURL(blobWithType);
                    const a = document.createElement('a');

                    a.href = url;
                    a.download = fileNameArray[0] + '.' + fileNameArray[1];

                    document.body.appendChild(a);

                    a.click();
                    window.URL.revokeObjectURL(a.href);
                    document.body.removeChild(a);
                });
            }
        });
}

function Form({transformToCart, invoiceId}: any): React.ReactElement {
    const form      = useForm();
    const errors    = form.formState.errors;
    const {id}      = useParams();
    const {t}       = useTranslation();
    const [throbber, setThrobber] = useState(false);
    const userContext = useContext(UserContext);
    const fileTypes = useFileTypes();

    function sendRequest(data: FieldValues){
        downloadFile(data, form.setError, parseInt(id ?? '0'), fileTypes, t);
    }

    return(
        <form onSubmit={form.handleSubmit(sendRequest)} >
            <h2>{t('user-page.invoices.download.title')}</h2>


            <div className={'field'+(errors.format ? ' error' : '')} >
                <label> {t('user-page.invoices.download.file_format')} {errors.format?.message && <span>{errors.format.message.toString()}</span>}</label>
                <select {...form.register('format', {required: t('user-page.invoices.download.error_format')})} defaultValue={userContext?.user ? userContext?.user.format : ''}  >

                    <option value={''} disabled={true} >-- {t('user-page.invoices.download.chose_format')}</option>
                    {Object.values(fileTypes).map((file: fileTypeData, key: number) => {
                        return <option key={key} value={file.format} >{file.label}</option>;
                    })}
                </select>
            </div>
            {form.watch('format') === fileTypes.EDIXML.format ?

                <div className={'field'+(errors.payer_code ? ' error' : '')} >
                    <label>{t('user-page.invoice.filters.payment-code')} {errors.payer_code?.message && <span>{errors.payer_code.message.toString()}</span>}</label>
                    <input {...form.register('payer_code')} value={userContext?.user ? userContext?.user.ediXMLPayerCode : ''} />
                </div>

                : null}

            <div className={'button'} >
                <Throbber visible={throbber} />
                <SavedCartModal setThrobber={setThrobber} endpoint={'/user/invoice/transform-to-cart'} idItem={invoiceId} />
                <button className={'btn'} >{t('user-page.invoices.file-to-download')}</button>
                <button className={'btn teal'} type={'button'} onClick={() => downloadFile({format: fileTypes.XLS.format}, form.setError, parseInt(id ?? '0'), fileTypes, t)} >XLS</button>
                <button className={'btn teal'} type={'button'} onClick={() => downloadFile({format: fileTypes.PDF.format}, form.setError, parseInt(id ?? '0'), fileTypes, t)} >PDF</button>
            </div>

            <p>{t('user-page.invoice.filters.info')}</p>
        </form>
    );
}

export {Form, useFileTypes};
export type { fileTypeData };
